import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { UserRole } from './roles';
import { registerSessionExpiredHandler, resetSessionExpiredGuard } from './api-client';
import { SCORM_LOGIN_FAILED_NOTICE, startScormSessionOnce, type ScormSessionResult } from './scorm-bridge';

interface User {
  id: string;
  email: string;
  role: UserRole;
  enterprise_id?: string;
  enterprise_name?: string;
  department?: string | null;
  /** Pseudo du classement, null tant que le joueur ne l'a pas choisi. */
  pseudo?: string | null;
  avatar_color?: string | null;
  /** Le workspace du joueur a activé le classement et les pseudos. */
  leaderboard_enabled?: boolean;
}

export const SESSION_EXPIRED_NOTICE =
  'Votre session a expiré. Veuillez vous reconnecter.';

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  updateSession: (user: User) => void;
  logout: () => void;
  isLoading: boolean;
  sessionNotice: string | null;
  clearSessionNotice: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function fetchCurrentUser(): Promise<User | null> {
  const me = await fetch('/api/auth/me');
  if (me.ok) {
    const data = await me.json() as { user?: User };
    return data.user ?? null;
  }

  if (me.status !== 401 && me.status !== 403) {
    return null;
  }

  const refreshed = await fetch('/api/auth/refresh', { method: 'POST' });
  if (!refreshed.ok) return null;

  const retry = await fetch('/api/auth/me');
  if (!retry.ok) return null;
  const data = await retry.json() as { user?: User };
  return data.user ?? null;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const logout = useCallback(() => {
    setUser(null);
    void fetch('/api/auth/logout', { method: 'POST' });
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    logout();
    setSessionNotice(SESSION_EXPIRED_NOTICE);
  }, [logout]);

  const clearSessionNotice = useCallback(() => {
    setSessionNotice(null);
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler(handleSessionExpired);
    return () => registerSessionExpiredHandler(() => {});
  }, [handleSessionExpired]);

  useEffect(() => {
    let cancelled = false;
    // Opened from an LMS: log the learner in from the launch page's identity first.
    startScormSessionOnce()
      .catch((): ScormSessionResult => 'failed')
      .then(async (scorm) => {
        const restored = await fetchCurrentUser();
        if (cancelled) return;
        if (restored) setUser(restored);
        else if (scorm === 'failed') setSessionNotice(SCORM_LOGIN_FAILED_NOTICE);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const refreshSession = () => {
      if (!userRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void fetchCurrentUser().then((restored) => {
        if (restored) setUser(restored);
      });
    };

    document.addEventListener('visibilitychange', refreshSession);
    window.addEventListener('focus', refreshSession);
    return () => {
      document.removeEventListener('visibilitychange', refreshSession);
      window.removeEventListener('focus', refreshSession);
    };
  }, []);

  const login = useCallback((newUser: User) => {
    resetSessionExpiredGuard();
    setSessionNotice(null);
    setUser(newUser);
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    } catch {
      // ignore storage errors
    }
  }, []);

  const updateSession = login;

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        updateSession,
        logout,
        isLoading,
        sessionNotice,
        clearSessionNotice,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
